import React, { useState } from 'react';
import { CheckCircle2, XCircle, RefreshCw, Trophy, BookOpen } from 'lucide-react';

const QuizComponent = ({ questions, title, color = '#E75480', onComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [quizCompleted, setQuizCompleted] = useState(false);

  const handleAnswerSelect = (answerIndex) => {
    if (showResult) return;
    setSelectedAnswer(answerIndex);
  };

  const handleValidate = () => {
    if (selectedAnswer === null) return;
    
    // Support both single answer (number) and multiple answers (array)
    const correctAnswer = questions[currentQuestion].correctAnswer;
    const isCorrect = Array.isArray(correctAnswer) 
      ? correctAnswer.includes(selectedAnswer)
      : selectedAnswer === correctAnswer;
    
    if (isCorrect) {
      setScore(score + 1);
    }
    
    setAnswers([...answers, { 
      question: currentQuestion, 
      selected: selectedAnswer, 
      correct: correctAnswer,
      isCorrect 
    }]);
    
    setShowResult(true);
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowResult(false);
    } else {
      setQuizCompleted(true);
      if (onComplete) {
        const correctAnswer = questions[currentQuestion].correctAnswer;
        const isCorrect = Array.isArray(correctAnswer)
          ? correctAnswer.includes(selectedAnswer)
          : selectedAnswer === correctAnswer;
        onComplete(score + (isCorrect ? 1 : 0), questions.length);
      }
    }
  };

  const handleRestart = () => {
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setScore(0);
    setAnswers([]);
    setQuizCompleted(false);
  };

  const getScoreMessage = () => {
    const percentage = (score / questions.length) * 100;
    if (percentage === 100) return { message: "Excellent ! Score parfait !", emoji: "🎉" };
    if (percentage >= 80) return { message: "Très bien ! Vous maîtrisez le sujet !", emoji: "👏" };
    if (percentage >= 60) return { message: "Bien ! Continuez à apprendre !", emoji: "👍" };
    return { message: "Révisez cette section pour progresser", emoji: "📚" };
  };

  if (quizCompleted) {
    const { message, emoji } = getScoreMessage();
    return (
      <div style={{
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        borderRadius: '20px',
        padding: '40px',
        textAlign: 'center',
        maxWidth: '600px',
        margin: '0 auto'
      }}>
        <Trophy size={60} color={color} style={{ marginBottom: '20px' }} />
        <h2 style={{ color: color, marginBottom: '10px', fontSize: '28px' }}>Quiz Terminé !</h2>
        <div style={{ 
          fontSize: '48px', 
          fontWeight: 'bold', 
          color: score >= questions.length * 0.6 ? '#10B981' : '#EF4444',
          marginBottom: '10px'
        }}>
          {score} / {questions.length}
        </div>
        <p style={{ fontSize: '18px', color: '#666', marginBottom: '20px' }}>
          {emoji} {message}
        </p>
        <button
          onClick={handleRestart}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            padding: '15px 30px',
            background: color,
            color: 'white',
            border: 'none',
            borderRadius: '30px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'transform 0.2s'
          }}
          onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
          onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
        >
          <RefreshCw size={20} />
          Recommencer le quiz
        </button>
      </div>
    );
  }

  const question = questions[currentQuestion];

  return (
    <div style={{
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
      borderRadius: '20px',
      padding: '30px',
      maxWidth: '800px',
      margin: '0 auto',
      boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '25px',
        paddingBottom: '15px',
        borderBottom: `2px solid ${color}20`
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <BookOpen size={24} color={color} />
          <span style={{ fontWeight: '600', color: color }}>{title}</span>
        </div>
        <div style={{
          background: `${color}15`,
          padding: '8px 15px',
          borderRadius: '20px',
          fontWeight: '600',
          color: color
        }}>
          Question {currentQuestion + 1} / {questions.length}
        </div>
      </div>

      {/* Progress bar */}
      <div style={{
        background: '#e9ecef',
        borderRadius: '10px',
        height: '8px',
        marginBottom: '25px',
        overflow: 'hidden'
      }}>
        <div style={{
          background: `linear-gradient(90deg, ${color}, ${color}aa)`,
          height: '100%',
          width: `${((currentQuestion + 1) / questions.length) * 100}%`,
          transition: 'width 0.3s ease'
        }} />
      </div>

      {/* Question */}
      <h3 style={{
        fontSize: '20px',
        color: '#333',
        marginBottom: '25px',
        lineHeight: '1.5'
      }}>
        {question.question}
      </h3>

      {/* Answers */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '25px' }}>
        {question.answers.map((answer, index) => {
          let bgColor = '#f8f9fa';
          let borderColor = '#dee2e6';
          let textColor = '#333';

          // Support both single answer (number) and multiple answers (array)
          const correctAnswer = question.correctAnswer;
          const isCorrectAnswer = Array.isArray(correctAnswer) 
            ? correctAnswer.includes(index)
            : index === correctAnswer;

          if (showResult) {
            if (isCorrectAnswer) {
              bgColor = '#d4edda';
              borderColor = '#10B981';
              textColor = '#155724';
            } else if (index === selectedAnswer && !isCorrectAnswer) {
              bgColor = '#f8d7da';
              borderColor = '#EF4444';
              textColor = '#721c24';
            }
          } else if (selectedAnswer === index) {
            bgColor = `${color}15`;
            borderColor = color;
            textColor = color;
          }

          return (
            <button
              key={index}
              onClick={() => handleAnswerSelect(index)}
              disabled={showResult}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
                padding: '15px 20px',
                background: bgColor,
                border: `2px solid ${borderColor}`,
                borderRadius: '12px',
                cursor: showResult ? 'default' : 'pointer',
                transition: 'all 0.2s',
                textAlign: 'left',
                fontSize: '16px',
                color: textColor
              }}
            >
              <span style={{
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                background: selectedAnswer === index || (showResult && isCorrectAnswer) 
                  ? borderColor 
                  : '#e9ecef',
                color: selectedAnswer === index || (showResult && isCorrectAnswer)
                  ? 'white'
                  : '#666',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '600',
                fontSize: '14px',
                flexShrink: 0
              }}>
                {String.fromCharCode(65 + index)}
              </span>
              <span style={{ flex: 1 }}>{answer}</span>
              {showResult && isCorrectAnswer && (
                <CheckCircle2 size={24} color="#10B981" />
              )}
              {showResult && index === selectedAnswer && !isCorrectAnswer && (
                <XCircle size={24} color="#EF4444" />
              )}
            </button>
          );
        })}
      </div>

      {/* Explanation (shown after answer) */}
      {showResult && question.explanation && (
        <div style={{
          background: '#e7f3ff',
          border: '1px solid #b6d4fe',
          borderRadius: '12px',
          padding: '15px 20px',
          marginBottom: '20px',
          color: '#084298'
        }}>
          <strong>Explication :</strong> {question.explanation}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '15px' }}>
        {!showResult ? (
          <button
            onClick={handleValidate}
            disabled={selectedAnswer === null}
            style={{
              padding: '12px 30px',
              background: selectedAnswer === null ? '#ccc' : color,
              color: 'white',
              border: 'none',
              borderRadius: '25px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: selectedAnswer === null ? 'not-allowed' : 'pointer',
              transition: 'transform 0.2s'
            }}
          >
            Valider ma réponse
          </button>
        ) : (
          <button
            onClick={handleNext}
            style={{
              padding: '12px 30px',
              background: color,
              color: 'white',
              border: 'none',
              borderRadius: '25px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'transform 0.2s'
            }}
          >
            {currentQuestion < questions.length - 1 ? 'Question suivante' : 'Voir mon score'}
          </button>
        )}
      </div>
    </div>
  );
};

export default QuizComponent;
